import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import {
  FlaggedMessage,
  FlaggedUserBlock,
  FlaggedUserDetail,
  FlaggedUserReport,
  MessageFlagMatch,
} from 'src/app/models/message-flag.model';
import { MessageFlagsService } from 'src/app/services/message-flags.service';
import { TimezoneService } from 'src/app/services/timezone.service';

/**
 * Per-sender breakdown behind one row of the Message Flags list: every flagged
 * message, the exact substring that tripped each rule, and the reports/blocks
 * other users filed against the same account.
 */
@Component({
  standalone: false,
  selector: 'sa-message-flag-detail',
  templateUrl: './message-flag-detail.component.html',
  styleUrls: ['./message-flag-detail.component.scss'],
})
export class MessageFlagDetailComponent implements OnInit, OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private messageFlagsService: MessageFlagsService,
    public timezoneService: TimezoneService,
  ) {}

  detail: FlaggedUserDetail | null = null;
  isLoading = true;
  hasError = false;

  /** Reason filter for the message list; empty means show everything. */
  activeReason = '';

  timezoneAbbr = 'PST';
  private timezoneSubscription: Subscription | null = null;
  private routeSubscription: Subscription | null = null;

  ngOnInit() {
    this.timezoneAbbr = this.timezoneService.getCurrentTimezoneAbbr();
    this.timezoneSubscription = this.timezoneService.getTimezoneConfig().subscribe(() => {
      this.timezoneAbbr = this.timezoneService.getCurrentTimezoneAbbr();
    });

    this.routeSubscription = this.route.paramMap.pipe(
      tap(() => {
        this.isLoading = true;
        this.hasError = false;
      }),
      switchMap(params => this.messageFlagsService
        .getFlaggedUserDetail(+params.get('userId'))
        .pipe(catchError(() => {
          this.hasError = true;
          return of(null as FlaggedUserDetail);
        }))),
    ).subscribe((detail) => {
      this.isLoading = false;
      this.detail = detail;
    });
  }

  ngOnDestroy() {
    if (this.timezoneSubscription) {
      this.timezoneSubscription.unsubscribe();
    }
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  get messages(): FlaggedMessage[] {
    if (!this.detail) {
      return [];
    }
    if (!this.activeReason) {
      return this.detail.messages;
    }
    return this.detail.messages.filter(
      message => message.reasons.some(reason => reason.reason_code === this.activeReason),
    );
  }

  displayName(): string {
    const user = this.detail && this.detail.user;
    if (!user) {
      return '';
    }
    return [user.name, user.lastname].filter(Boolean).join(' ') || `#${user.user_id}`;
  }

  profileImage(): string | null {
    const user = this.detail && this.detail.user;
    const images = user && user.profile_images ? user.profile_images : [];
    return images.length ? images[0].image_url : null;
  }

  recipientLabel(message: FlaggedMessage): string {
    if (!message.to_user) {
      return `#${message.to_user_id}`;
    }
    const name = [message.to_user.name, message.to_user.lastname].filter(Boolean).join(' ');
    return name ? `${name} (#${message.to_user_id})` : `#${message.to_user_id}`;
  }

  /**
   * Splits the message around the substring that tripped a rule so the match
   * can be highlighted in place. Falls back to the whole text unhighlighted
   * when the stored match no longer appears (e.g. duplicate-rule matches,
   * whose matched_text is a summary rather than a literal excerpt).
   */
  highlightParts(text: string, reasons: MessageFlagMatch[]): { text: string; match: boolean }[] {
    // A match longer than the stored column is kept with a trailing ellipsis,
    // so fall back to the part that did survive rather than showing "matched:
    // ..." next to an unhighlighted message.
    const candidates = (reasons || [])
      .map(reason => reason.matched_text)
      .filter(Boolean)
      .reduce((acc, matched) => acc.concat(
        matched.endsWith('…') ? [matched, matched.slice(0, -1)] : [matched],
      ), [] as string[]);

    const literal = candidates
      .find(matched => text && text.toLowerCase().includes(matched.toLowerCase()));

    if (!literal) {
      return [{ text: text || '', match: false }];
    }

    const start = text.toLowerCase().indexOf(literal.toLowerCase());
    return [
      { text: text.slice(0, start), match: false },
      { text: text.slice(start, start + literal.length), match: true },
      { text: text.slice(start + literal.length), match: false },
    ].filter(part => part.text.length > 0);
  }

  /** Blocker display name, falling back to the id when the row is gone. */
  blockerName(block: FlaggedUserBlock): string {
    const blocker = block.blocker;
    if (!blocker) {
      return `#${block.user_id}`;
    }
    const name = [blocker.name, blocker.lastname].filter(Boolean).join(' ');
    return name || `#${block.user_id}`;
  }

  /** Reporter display name, falling back to the id when the row is gone. */
  reporterName(report: FlaggedUserReport): string {
    const reporter = report.reporter;
    if (!reporter) {
      return `#${report.reporting_user_id}`;
    }
    const name = [reporter.name, reporter.lastname].filter(Boolean).join(' ');
    return name || `#${report.reporting_user_id}`;
  }

  reasonFilterChanged(reasonCode: string) {
    this.activeReason = reasonCode;
  }

  backToList() {
    this.router.navigate(['/message-flags']);
  }

  openUser() {
    if (this.detail && this.detail.user) {
      this.router.navigate(['/users', this.detail.user.user_id]);
    }
  }
}
